import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { FormModal, Field } from "../pages/LeadsPage";
import { toast } from "sonner";
import { Copy, Check, Link2, ExternalLink, Loader2 } from "lucide-react";

export default function SendInvoiceModal({ invoice, onClose, onRefresh }) {
  const [email, setEmail] = useState("");
  const [payUrl, setPayUrl] = useState(invoice.payment_url || "");
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  useEffect(() => {
    if (!payUrl) generateLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateLink = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/create-payment`, {
        origin_url: window.location.origin,
      });
      setPayUrl(data.checkout_url);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create payment link");
    } finally {
      setCreating(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setEmailResult(null);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/send`, { recipient_email: email });
      setEmailResult(data);
      if (data.email?.sent) toast.success("Invoice emailed");
      else if (!data.email_configured) toast.info("Email not configured — share link manually");
      else toast.error(data.email?.reason || "Email failed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSending(false);
    }
  };

  const copy = async () => {
    if (!payUrl) return;
    await navigator.clipboard.writeText(payUrl);
    setCopied(true);
    toast.success("Payment link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <FormModal title={`Send invoice — $${Number(invoice.amount).toLocaleString()}`} onClose={onClose} onSubmit={send}>
      <div className="text-sm text-slate-400 -mt-1 mb-2">
        Stripe Checkout (test mode). Customer pays securely on Stripe-hosted page.
      </div>

      <Field label="Recipient email" type="email" value={email} onChange={setEmail} testId="send-invoice-email" />

      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-label">Stripe payment link</div>
          {!creating && payUrl && (
            <a href={payUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-300 flex items-center gap-1 hover:text-blue-200">
              Open <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
        {creating ? (
          <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Creating link…</div>
        ) : payUrl ? (
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-slate-300 truncate" data-testid="invoice-pay-url">
              {payUrl}
            </code>
            <button type="button" onClick={copy} data-testid="invoice-pay-copy" className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs flex items-center gap-1">
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>
        ) : (
          <button type="button" onClick={generateLink} className="text-sm bg-white text-black px-4 py-2 rounded-full font-semibold hover:bg-slate-100">
            Generate payment link
          </button>
        )}
        <div className="flex items-center gap-1 text-[10px] text-slate-500">
          <Link2 className="w-3 h-3" /> Test card: 4242 4242 4242 4242 — any future date / any CVC.
        </div>
      </div>

      {emailResult && (
        <div className={`text-xs rounded-xl px-3 py-2 ${emailResult.email?.sent ? "bg-emerald-500/10 text-emerald-300 border border-emerald-400/20" : "bg-amber-500/10 text-amber-300 border border-amber-400/20"}`}>
          {emailResult.email?.sent
            ? `Email sent (id: ${emailResult.email.id})`
            : emailResult.email_configured
              ? `Email failed: ${emailResult.email.reason}`
              : "Email not configured. Copy the link above and share manually."}
        </div>
      )}
    </FormModal>
  );
}
